import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, Check, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface OrientationScreenProps {
  onNext: () => void;
  onBack?: () => void;
}
export function OrientationScreen({
  onNext,
  onBack
}: OrientationScreenProps) {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const steps = ['Importing template...', 'Setting up pages...', 'Configuring actions...', 'Final touches...'];
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= steps.length) {
          clearInterval(timer);
          setIsComplete(true);
          return prev;
        }
        return prev + 1;
      });
    }, 2000);
    return () => clearInterval(timer);
  }, []);
  return <div className="min-h-screen flex flex-col items-center justify-center p-6 pb-20 bg-[var(--bg)] transition-colors duration-300" data-id="element-121">
      <div className="max-w-3xl w-full flex flex-col items-center" data-id="element-122">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-8 w-full" data-id="element-123">
          <AnimatePresence mode="wait" data-id="element-124">
            {!isComplete ? <motion.div key="building" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} data-id="element-125">
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-4 transition-colors duration-300" data-id="element-126">
                  Building your funnel
                </h2>
                {/* Animated progress bar */}
                <div className="w-48 h-1 bg-[var(--border)] rounded-full mx-auto mb-4 overflow-hidden" data-id="element-127">
                  <motion.div className="h-full bg-[var(--text-secondary)] rounded-full origin-left" initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 8,
                ease: 'linear'
              }} data-id="element-128" />
                </div>
              </motion.div> : <motion.h2 key="complete" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-4 transition-colors duration-300 flex items-center justify-center gap-3" data-id="element-129">
                Your funnel is ready!{' '}
                <Sparkles className="text-[var(--accent-gold)]" data-id="element-130" />
              </motion.h2>}
          </AnimatePresence>
          <p className="text-[var(--text-secondary)] transition-colors duration-300" data-id="element-131">
            While we set things up, check out what's possible with ClickFunnels.
          </p>
        </motion.div>

        {/* Video Player Placeholder */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2
      }} className="w-full aspect-video bg-[#1a1f36] rounded-2xl shadow-2xl shadow-[var(--shadow-lg)] relative group cursor-pointer overflow-hidden mb-10 border border-[var(--border)]" data-id="element-132">
          {/* Thumbnail Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f36] to-[#0f172a] flex items-center justify-center" data-id="element-133">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20 mix-blend-overlay" data-id="element-134" />

            {/* Play Button */}
            <motion.div whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.95
          }} className="w-20 h-20 rounded-full bg-[var(--accent-gold)] flex items-center justify-center shadow-lg shadow-[var(--accent-gold)]/30 z-10 relative" data-id="element-135">
              <Play fill="#1a1f36" className="text-[#1a1f36] ml-1" size={32} data-id="element-136" />

              {/* Pulse Effect */}
              <div className="absolute inset-0 rounded-full bg-[var(--accent-gold)] animate-ping opacity-20" data-id="element-137" />
            </motion.div>
          </div>

          {/* Video Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0f172a]/90 to-transparent" data-id="element-138">
            <div className="flex items-center justify-between" data-id="element-139">
              <div data-id="element-140">
                <h3 className="text-white font-bold text-lg mb-1" data-id="element-141">
                  Quick tour: The basics of ClickFunnels
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-142">
                  Learn the fundamentals in 2 minutes
                </p>
              </div>
              <span className="bg-black/40 text-white text-xs font-medium px-2 py-1 rounded backdrop-blur-sm border border-white/10" data-id="element-143">
                2:14
              </span>
            </div>
          </div>
        </motion.div>

        {/* Build Progress Steps */}
        <div className="w-full max-w-2xl flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-10" data-id="element-144">
          {steps.map((step, index) => {
          const isStepComplete = progress > index;
          const isStepActive = progress === index;
          return <motion.div key={step} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4 + index * 0.1
          }} className="flex items-center gap-2 transition-colors duration-300" data-id="element-145">
                <div className="relative flex items-center justify-center w-5 h-5" data-id="element-146">
                  <AnimatePresence mode="wait" data-id="element-147">
                    {isStepComplete ? <motion.div key="check" initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="text-[var(--accent-gold)]" data-id="element-148">
                        <Check size={16} strokeWidth={3} data-id="element-149" />
                      </motion.div> : isStepActive ? <motion.div key="loading" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="text-[var(--text-primary)]" data-id="element-150">
                        <Loader2 size={16} className="animate-spin" data-id="element-151" />
                      </motion.div> : <motion.div key="waiting" className="w-1.5 h-1.5 rounded-full bg-[var(--border)]" data-id="element-152" />}
                  </AnimatePresence>
                </div>

                <span className={`text-xs font-medium whitespace-nowrap transition-colors duration-300 ${isStepComplete ? 'text-[var(--text-muted)] line-through decoration-[var(--border)]' : isStepActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)] opacity-50'}`} data-id="element-153">
                  {step}
                </span>
              </motion.div>;
        })}
        </div>

        {/* CTA Area */}
        <AnimatePresence data-id="element-154">
          {isComplete && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex flex-col items-center gap-3" data-id="element-155">
              <Button size="lg" onClick={onNext} className="px-12 font-bold text-lg" data-id="element-156">
                Open your funnel
              </Button>
              <p className="text-sm text-[var(--text-secondary)]" data-id="element-157">
                Your funnel is ready to customize
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Back Button - Centered at Bottom */}
      {onBack && !isComplete && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1
    }} className="flex justify-center pt-8" data-id="element-158">
          <button onClick={onBack} className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] text-sm font-medium transition-colors" data-id="element-159">
            <ArrowLeft size={16} data-id="element-160" />
            Back
          </button>
        </motion.div>}
    </div>;
}